import { useCallback, useEffect, useRef, useState } from "react";
import {
  ANNOUNCEMENTS,
  type AnnouncementContext,
  type AnnouncementDisplay,
  type AnnouncementPlacement,
  type FeatureAnnouncementEntry,
} from "./registry";
import { guidanceForced } from "@/lib/guidance-override";
import { type DismissalMap, dismissAnnouncement, readDismissals } from "./store";

/**
 * `?announcement=1` replays every announcement whose slot is on the page,
 * ignoring dates, dismissal, audience, and the once-per-session cap.
 * Passing an id, such as `?announcement=introducing-pages`, targets one.
 */
export function announcementForced(id: string): boolean {
  return guidanceForced("announcement", id);
}

/**
 * Picks the announcement a host should show, if any. Checks, in order:
 * display and placement match, `publishedAt` reached, not expired, not
 * dismissed, `eligible(ctx)`. Of the survivors, the single newest one wins;
 * older undismissed survivors queue for later visits instead of stacking.
 *
 * A forced entry (see `announcementForced`) only has to match the slot and
 * beats every organic survivor.
 */
export function resolveAnnouncement(options: {
  display: AnnouncementDisplay;
  placement: AnnouncementPlacement;
  context: AnnouncementContext;
  dismissed: DismissalMap;
  now: Date;
  entries?: FeatureAnnouncementEntry[];
  forced?: (id: string) => boolean;
}): FeatureAnnouncementEntry | null {
  const entries = options.entries ?? ANNOUNCEMENTS;
  const forced = options.forced ?? (() => false);

  const survivors = entries.filter((entry) => {
    if (entry.display !== options.display) return false;
    if (entry.placement !== options.placement) return false;
    if (forced(entry.id)) return true;
    if (options.now < new Date(entry.publishedAt)) return false;
    if (options.now >= new Date(entry.expiresAt)) return false;
    if (options.dismissed[entry.id]) return false;
    return entry.eligible(options.context);
  });

  if (survivors.length === 0) return null;

  const forcedEntry = survivors.find((entry) => forced(entry.id));
  if (forcedEntry) return forcedEntry;

  let newest = survivors[0];
  for (const entry of survivors) {
    if (entry.publishedAt > newest.publishedAt) newest = entry;
  }
  return newest;
}

/**
 * Resolution hook a host calls with its slot and already-fetched context.
 * SSR-safe: resolves after mount (the dismissal store is client-only), so the
 * server and first client render both show nothing. `ready` lets hosts hold
 * resolution until async context (flags, integrations) has loaded; once
 * resolved, the result latches so dismissing hides the announcement without
 * surfacing the next queued one until a later visit. A forced entry (QA
 * override) skips the `ready` wait, since it ignores the context anyway.
 */
export function useAnnouncement(options: {
  display: AnnouncementDisplay;
  placement: AnnouncementPlacement;
  context: AnnouncementContext;
  ready?: boolean;
}): { announcement: FeatureAnnouncementEntry | null; dismiss: () => void } {
  const [announcement, setAnnouncement] = useState<FeatureAnnouncementEntry | null>(null);
  const resolvedRef = useRef(false);
  const ready = options.ready ?? true;
  const { display, placement, context } = options;

  useEffect(() => {
    if (resolvedRef.current) return;
    const forcedInSlot = ANNOUNCEMENTS.some(
      (entry) =>
        entry.display === display && entry.placement === placement && announcementForced(entry.id),
    );
    if (!ready && !forcedInSlot) return;
    resolvedRef.current = true;
    const knownIds = new Set(ANNOUNCEMENTS.map((entry) => entry.id));
    setAnnouncement(
      resolveAnnouncement({
        display,
        placement,
        context,
        dismissed: readDismissals(knownIds),
        now: new Date(),
        forced: announcementForced,
      }),
    );
  }, [ready, display, placement, context]);

  const dismiss = useCallback(() => {
    setAnnouncement((current) => {
      if (current) dismissAnnouncement(current.id);
      return null;
    });
  }, []);

  return { announcement, dismiss };
}
